'use client';
import React from 'react';
import { Globe, ChevronDown } from 'lucide-react';

const LANGUAGES = [
  { key: 'lang-en', code: 'EN', label: 'English' },
  { key: 'lang-fr', code: 'FR', label: 'Français' },
  { key: 'lang-ar', code: 'AR', label: 'العربية' },
];

const CURRENCIES = [
  { key: 'cur-dzd', code: 'DZD', symbol: 'د.ج' },
  { key: 'cur-eur', code: 'EUR', symbol: '€' },
  { key: 'cur-usd', code: 'USD', symbol: '$' },
];

interface NavDropdownProps {
  isOpen: boolean;
  onToggle: () => void;
  selectedLang: string;
  selectedCurrency: string;
  onSelectLang: (code: string) => void;
  onSelectCurrency: (code: string) => void;
}

export default function NavDropdown({
  isOpen,
  onToggle,
  selectedLang,
  selectedCurrency,
  onSelectLang,
  onSelectCurrency,
}: NavDropdownProps) {
  return (
    <div className="relative hidden md:block">
      <button
        onClick={onToggle}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-display font-500 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-150"
        style={{ fontWeight: 500 }}
        aria-label="Language and currency selector"
      >
        <Globe size={16} />
        <span>{selectedLang}</span>
        <span className="text-gray-300">·</span>
        <span>{selectedCurrency}</span>
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white dark:bg-gray-900 rounded-2xl card-shadow-lg border border-gray-100 dark:border-gray-700 py-2 z-50 animate-slide-up">
          <div className="px-3 py-1.5">
            <p
              className="text-xs font-display font-600 text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1"
              style={{ fontWeight: 600 }}
            >
              Language
            </p>
            {LANGUAGES?.map((lang) => (
              <button
                key={lang?.key}
                onClick={() => onSelectLang(lang?.code)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-body transition-colors duration-150
                  ${selectedLang === lang?.code
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-600' :'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                {lang?.label}
              </button>
            ))}
          </div>
          <div className="border-t border-gray-100 dark:border-gray-700 mt-1 px-3 py-1.5">
            <p
              className="text-xs font-display font-600 text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-1"
              style={{ fontWeight: 600 }}
            >
              Currency
            </p>
            {CURRENCIES?.map((cur) => (
              <button
                key={cur?.key}
                onClick={() => onSelectCurrency(cur?.code)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm font-body transition-colors duration-150
                  ${selectedCurrency === cur?.code
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 font-600' :'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
              >
                {cur?.symbol} {cur?.code}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
